import React from 'react';
import styled, { css } from 'styled-components';
import { User } from 'lucide-react';
import { theme } from '../../tokens';

const avatarSizes = {
  xs: css`
    width: 24px;
    height: 24px;
    font-size: 10px;

    svg {
      width: 14px;
      height: 14px;
    }
  `,
  sm: css`
    width: 32px;
    height: 32px;
    font-size: ${theme.fontSizes.xs};

    svg {
      width: 16px;
      height: 16px;
    }
  `,
  md: css`
    width: 40px;
    height: 40px;
    font-size: ${theme.fontSizes.sm};

    svg {
      width: 20px;
      height: 20px;
    }
  `,
  lg: css`
    width: 56px;
    height: 56px;
    font-size: ${theme.fontSizes.lg};

    svg {
      width: 28px;
      height: 28px;
    }
  `,
  xl: css`
    width: 80px;
    height: 80px;
    font-size: ${theme.fontSizes['2xl']};

    svg {
      width: 40px;
      height: 40px;
    }
  `,
  '2xl': css`
    width: 120px;
    height: 120px;
    font-size: ${theme.fontSizes['4xl']};

    svg {
      width: 60px;
      height: 60px;
    }
  `,
};

const StyledAvatar = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ $squared }) => ($squared ? theme.radii.lg : theme.radii.full)};
  background: ${theme.gradients.primary};
  color: ${theme.colors.text.inverse};
  font-weight: ${theme.fontWeights.semibold};
  overflow: hidden;
  flex-shrink: 0;

  ${({ $size }) => avatarSizes[$size] || avatarSizes.md}

  ${({ $bordered }) =>
    $bordered &&
    css`
      border: 3px solid ${theme.colors.background.primary};
      box-shadow: ${theme.shadows.md};
    `}
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StatusIndicator = styled.span`
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${({ $size }) => {
    switch ($size) {
      case 'xs':
        return '8px';
      case 'sm':
        return '10px';
      case 'lg':
        return '14px';
      case 'xl':
        return '18px';
      case '2xl':
        return '24px';
      default:
        return '12px';
    }
  }};
  height: ${({ $size }) => {
    switch ($size) {
      case 'xs':
        return '8px';
      case 'sm':
        return '10px';
      case 'lg':
        return '14px';
      case 'xl':
        return '18px';
      case '2xl':
        return '24px';
      default:
        return '12px';
    }
  }};
  border-radius: ${theme.radii.full};
  border: 2px solid ${theme.colors.background.primary};

  ${({ $status }) => {
    switch ($status) {
      case 'online':
        return css`
          background: ${theme.colors.success[500]};
        `;
      case 'offline':
        return css`
          background: ${theme.colors.neutral[400]};
        `;
      case 'busy':
        return css`
          background: ${theme.colors.error[500]};
        `;
      case 'away':
        return css`
          background: ${theme.colors.warning[500]};
        `;
      default:
        return '';
    }
  }}
`;

const AvatarGroup = styled.div`
  display: flex;
  flex-direction: row-reverse;

  > * {
    margin-left: -${theme.spacing[2]};

    &:last-child {
      margin-left: 0;
    }
  }
`;

const OverflowIndicator = styled(StyledAvatar)`
  background: ${theme.colors.neutral[200]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
`;

const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export const Avatar = React.forwardRef(
  ({ src, alt, name, size = 'md', squared = false, bordered = false, status, fallback, ...props }, ref) => {
    const [imageError, setImageError] = React.useState(false);

    const showFallback = !src || imageError;
    const initials = name ? getInitials(name) : null;

    return (
      <StyledAvatar ref={ref} $size={size} $squared={squared} $bordered={bordered} {...props}>
        {!showFallback ? (
          <AvatarImage src={src} alt={alt || name || 'Avatar'} onError={() => setImageError(true)} />
        ) : initials ? (
          initials
        ) : fallback ? (
          fallback
        ) : (
          <User />
        )}
        {status && <StatusIndicator $status={status} $size={size} />}
      </StyledAvatar>
    );
  }
);

Avatar.displayName = 'Avatar';

Avatar.Group = ({ children, max, size = 'md', ...props }) => {
  const childArray = React.Children.toArray(children);
  const visibleCount = max ? Math.min(childArray.length, max) : childArray.length;
  const overflowCount = childArray.length - visibleCount;

  return (
    <AvatarGroup {...props}>
      {overflowCount > 0 && (
        <OverflowIndicator $size={size} $bordered>
          +{overflowCount}
        </OverflowIndicator>
      )}
      {childArray.slice(0, visibleCount).reverse().map((child, index) =>
        React.cloneElement(child, {
          key: index,
          size,
          bordered: true,
        })
      )}
    </AvatarGroup>
  );
};

export default Avatar;
